import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, KanbanSquare, Mail, Building2, Check } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const HERO_BG = "https://images.unsplash.com/photo-1767028071770-e775364a93ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBzcG9ydHMlMjBiYWNrZ3JvdW5kJTIwc3RhZGl1bXxlbnwwfHx8fDE3Nzg3MTY0MzB8MA&ixlib=rb-4.1.0&q=85";
const ATHLETE_IMG = "https://images.unsplash.com/photo-1609377375724-8fadc82cd50e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBhdGhsZXRlJTIwcnVubmluZyUyMGRhcmt8ZW58MHx8fHwxNzc4NzE2NDI1fDA&ixlib=rb-4.1.0&q=85";
const GYM_IMG = "https://images.unsplash.com/photo-1637870473618-8c9fa7d11f0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxneW0lMjBkYXJrJTIwbmVvbiUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3ODcxNjQzMHww&ixlib=rb-4.1.0&q=85";

function Header() {
  return (
    <header className="fixed top-0 w-full z-50 bg-bg/85 backdrop-blur-xl border-b border-line">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
        <Link to="/" className="flex items-center" data-testid="landing-logo">
          <BrandLogo variant="wordmark" size={28} showLink={false} />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-widest text-ink-muted">
          <a href="#how" className="hover:text-white transition-colors">How it works</a>
          <a href="#features" className="hover:text-white transition-colors">Features</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          <Link to="/for-brands" className="hover:text-volt transition-colors" data-testid="landing-for-brands-link">For brands</Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-xs uppercase tracking-widest text-ink-muted hover:text-white" data-testid="landing-login-link">Sign in</Link>
          <Link to="/register" className="btn-volt text-xs" data-testid="landing-cta-header">Start free</Link>
        </div>
      </div>
    </header>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-bg text-white">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-bg via-bg/60 to-bg" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 animate-fade-up">
            <div className="label-eyebrow mb-6">The AI Operating System For Athlete Growth</div>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl leading-[0.95] uppercase font-bold tracking-tighter">
              Build a commercial career<br />
              around your <span className="text-volt">athletic identity.</span>
            </h1>
            <p className="mt-8 text-lg text-ink-muted max-w-xl leading-relaxed">
              Most athletes wait for sponsors to find them. PITCHD makes you findable — AI scores your sponsorship readiness, builds your editorial media kit, matches you with brands, and writes the outreach that lands deals.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register" className="btn-volt inline-flex items-center gap-2" data-testid="hero-create-profile-btn">
                Get my Positioning Report <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#features" className="btn-ghost-line inline-flex items-center gap-2" data-testid="hero-demo-btn">
                See how it works
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-ink-dim">
              <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-volt" /> Free to start</span>
              <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-volt" /> No agent needed</span>
              <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-volt" /> Setup in 4 mins</span>
            </div>
          </div>
          <div className="md:col-span-5 hidden md:block">
            <div className="relative">
              <div className="absolute inset-0 border border-volt translate-x-3 translate-y-3" />
              <div className="relative aspect-[4/5] overflow-hidden border border-line">
                <img src={ATHLETE_IMG} alt="Athlete" className="w-full h-full object-cover" />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-bg to-transparent">
                  <div className="label-eyebrow mb-1">Illustrative profile preview</div>
                  <div className="font-heading text-2xl uppercase">Your sponsorship pipeline</div>
                  <div className="text-xs text-ink-muted mt-1">Track every brand · See replies in real time</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 border-t border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow">How it works</div>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-4 max-w-3xl">Four steps from no-name to sponsor-ready.</h2>
          <div className="grid md:grid-cols-4 gap-4 mt-12">
            {[
              {n:"01", t:"Build your profile", d:"Add your sport, stats, audience and story in under 4 minutes."},
              {n:"02", t:"Generate AI media kit", d:"Sponsor-grade bio, value prop and packages — written by AI."},
              {n:"03", t:"Add brand targets", d:"Curate the brands that fit your story. Add notes, contacts and value."},
              {n:"04", t:"Send AI outreach", d:"Personalized email, IG DM or LinkedIn pitch. Track every reply."},
            ].map((s) => (
              <div key={s.n} className="surface p-6">
                <div className="font-mono text-xs text-volt mb-4">{s.n}</div>
                <h3 className="font-heading text-xl uppercase mb-2">{s.t}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 border-t border-line bg-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-7">
              <div className="label-eyebrow">Features</div>
              <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-4">Built like a war-room. Feels like a manager in your pocket.</h2>
            </div>
            <div className="md:col-span-5 text-ink-muted text-sm leading-relaxed">
              Stop sending generic DMs. PITCHD turns your career into a structured sponsorship machine — kit, pipeline, outreach, follow-ups.
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-4">
            <div className="md:col-span-7 surface p-8 relative overflow-hidden">
              <Sparkles className="w-6 h-6 text-volt mb-6" />
              <h3 className="font-heading text-2xl uppercase">AI Media Kit Generator</h3>
              <p className="text-ink-muted mt-2 text-sm leading-relaxed max-w-lg">Pro athlete bio, audience summary, sponsorship value prop, packages and collab ideas — all generated from your profile in seconds.</p>
              <div className="mt-6 grid grid-cols-3 gap-2">
                {["Bio","Packages","Collab"].map(t => <div key={t} className="border border-line p-3 text-xs label-eyebrow text-volt">{t}</div>)}
              </div>
            </div>
            <div className="md:col-span-5 surface p-8">
              <KanbanSquare className="w-6 h-6 text-volt mb-6" />
              <h3 className="font-heading text-2xl uppercase">Sponsorship CRM</h3>
              <p className="text-ink-muted mt-2 text-sm leading-relaxed">Kanban for brand deals. Target → Contacted → Replied → Negotiating → Won. Track value and follow-ups.</p>
            </div>
            <div className="md:col-span-5 surface p-8">
              <Mail className="w-6 h-6 text-volt mb-6" />
              <h3 className="font-heading text-2xl uppercase">AI Outreach</h3>
              <p className="text-ink-muted mt-2 text-sm leading-relaxed">Pick a brand, channel and tone. Get a personalized pitch + follow-up. Email, IG DM or LinkedIn.</p>
            </div>
            <div className="md:col-span-7 surface p-0 relative overflow-hidden">
              <img src={GYM_IMG} alt="Gym" className="w-full h-64 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/60 to-transparent flex items-center">
                <div className="p-8 max-w-md">
                  <Building2 className="w-6 h-6 text-volt mb-4" />
                  <h3 className="font-heading text-2xl uppercase">Brand Database</h3>
                  <p className="text-ink-muted mt-2 text-sm">Build your shortlist of dream sponsors with notes, priority and fit reasons.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 border-t border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow">Pricing · For athletes &amp; agencies</div>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-4 max-w-3xl">One subscription. Every deal.</h2>
          <p className="text-ink-muted mt-3 max-w-2xl text-sm">Start free, upgrade when your sponsorship game outgrows it. Built for individual athletes and the agencies that manage them.</p>
          <div className="grid md:grid-cols-3 gap-4 mt-12">
            {[
              {name:"Free", price:"£0", sub:"Forever", feats:["1 media kit","5 brand targets","Basic AI outreach"], cta:"Start free", primary:false},
              {name:"Pro", price:"£19", sub:"per month", feats:["Unlimited outreach","50 brand targets","Media kit PDF exports","Priority AI"], cta:"Go pro", primary:true},
              {name:"Agency", price:"£99", sub:"per month", feats:["Manage multiple athletes","Unlimited everything","White-label exports","Priority support"], cta:"Talk to us", primary:false},
            ].map((p) => (
              <div key={p.name} className={`p-8 border ${p.primary ? "border-volt bg-bg-elev" : "border-line bg-bg-surface"} relative`} data-testid={`pricing-${p.name.toLowerCase()}-card`}>
                {p.primary && <div className="absolute -top-3 left-8 bg-volt text-black text-[10px] tracking-widest uppercase px-2 py-1 font-bold">Most popular</div>}
                <div className="font-heading text-3xl uppercase">{p.name}</div>
                <div className="mt-4"><span className="font-heading text-5xl">{p.price}</span><span className="text-ink-muted ml-2 text-sm">{p.sub}</span></div>
                <ul className="mt-6 space-y-3 text-sm">
                  {p.feats.map(f => <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-volt mt-0.5 shrink-0" />{f}</li>)}
                </ul>
                <Link to="/register" className={`mt-8 inline-flex w-full justify-center ${p.primary ? "btn-volt" : "btn-ghost-line"}`}>{p.cta}</Link>
              </div>
            ))}
          </div>

          {/* For-Brands callout — premium, lightweight, single-line story */}
          <Link to="/for-brands" data-testid="landing-brand-callout"
            className="group mt-10 flex flex-wrap items-center justify-between gap-4 border border-line hover:border-volt transition-colors px-6 py-5 bg-bg-surface">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-10 h-10 border border-volt/40 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-4 h-4 text-volt" />
              </div>
              <div className="min-w-0">
                <div className="font-heading text-lg uppercase tracking-tight">For Brands</div>
                <p className="text-xs text-ink-muted mt-0.5">Discover, evaluate and manage athlete partnerships at scale — purpose-built brand workspace.</p>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-volt group-hover:translate-x-0.5 transition-transform">
              Explore brand plans <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </Link>
        </div>
      </section>

      {/* Capabilities band — replaces athlete-testimonial placeholders.
          We'll re-introduce real athlete quotes only with written consent. */}
      <section className="py-24 border-t border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow">Built for the modern athlete</div>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-4 max-w-3xl">From Sunday league to season partners.</h2>
          <p className="text-ink-muted text-sm md:text-base max-w-2xl mt-4 leading-relaxed">
            PITCHD gives you what professional athletes pay agents to do — kit, pipeline, outreach and follow-ups — in a single workspace you control. Below are the four pillars every athlete profile is built on.
          </p>
          <div className="grid md:grid-cols-2 gap-4 mt-12">
            {[
              {n:"01", t:"Editorial media kit", d:"A 5-page AI-generated kit brands actually want to open — designed to read like a magazine feature, not a CV."},
              {n:"02", t:"Sponsor-ready scoring", d:"PITCHD scores your sponsor readiness across audience, engagement, niche fit and content cadence — and tells you exactly what to improve."},
              {n:"03", t:"AI outreach that respects the brand", d:"Tailored opening messages per sponsor, drafted from your profile and the brand's category. No copy-pasted DMs."},
              {n:"04", t:"Pipeline + replies in one place", d:"Track every brand, every send, every reply and every contract from a single CRM built for athlete commercial work."},
            ].map(item => (
              <div key={item.n} className="surface p-6 md:p-7">
                <div className="label-eyebrow text-volt">{item.n}</div>
                <h3 className="font-heading text-xl uppercase tracking-tight mt-2">{item.t}</h3>
                <p className="text-sm text-ink-muted leading-relaxed mt-2">{item.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 border-t border-line">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-5xl md:text-6xl uppercase tracking-tighter">Stop chasing brands.<br /><span className="text-volt">Start signing them.</span></h2>
          <p className="mt-6 text-ink-muted max-w-xl mx-auto">Your sponsorship workspace is one click away.</p>
          <Link to="/register" className="btn-volt mt-10 inline-flex items-center gap-2" data-testid="cta-bottom-btn">Create free profile <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      <footer className="border-t border-line py-10" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center text-xs text-ink-dim gap-4">
          <div className="flex items-center gap-2 uppercase tracking-widest">
            <BrandLogo variant="icon" size={14} showLink={false} className="opacity-80" alt="PITCHD" data-testid="footer-logo" />
            <span>© 2026 PITCHD. All rights reserved.</span>
            <span className="hidden md:inline text-ink-dim/70">·</span>
            <span className="hidden md:inline normal-case tracking-normal">pitchdhq.com</span>
          </div>
          <div className="flex flex-wrap items-center gap-5 uppercase tracking-widest">
            <a href="/terms" className="hover:text-volt" data-testid="footer-terms">Terms</a>
            <a href="/privacy" className="hover:text-volt" data-testid="footer-privacy">Privacy</a>
            <a href="/commercial-terms" className="hover:text-volt" data-testid="footer-commercial">Commercial Terms</a>
            <a href="mailto:hello@pitchdhq.com" className="hover:text-volt" data-testid="footer-contact">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
